import { useState } from 'react'
import { api } from 'lib/axios'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setCurrentTab } from 'slices/customerSteps'
import { setNewCustomer } from 'slices/customer'
import { useLogActivity } from './useLogActivity'
import { getUserId } from 'lib/auth/tokenStorage'

const createCustomer = async data => {
  const agent_id = getUserId() || ''
  const formData = new FormData()

  formData.append('method', 'POST')
  formData.append('agent_id', agent_id)
  formData.append('customer[first_name]', data.first_name)
  formData.append('customer[last_name]', data.last_name)
  formData.append('customer[agent_id_c]', agent_id)
  formData.append('trip_type', data.trip_type)
  formData.append('customer[previous_status_c]', 'refund_request_created')

  const response = await api.post(
    '/Api/V8/custom/customer/create-new-customer',
    formData
  )
  return response.data
}

const createShareFileFolder = async contactId => {
  const response = await api.post(
    '/Api/V8/custom/customer/create-sharefile-folder',
    {
      contact_id: contactId
    }
  )
  return response.data
}

const handleSyncDataApi = async contactId => {
  const response = await api.post(
    '/Api/V8/custom/customer/handle-contact-sharefile',
    { contact_id: contactId }
  )
  return response
}

const deleteCustomer = async id => {
  const response = await api.post('/Api/V8/custom/customer/delete-customer', {
    data: {
      customer_id: id
    }
  })
  return response.data
}

const claimCustomer = async (contactId, agentId) => {
  const response = await api.post('/Api/V8/custom/customer/claim-customer', {
    agent_id: agentId,
    data: {
      contact_id: contactId,
      agent_id: agentId,
      affiliate_id: '',
      region_id: '',
      location_store_id: '',
      new_affiliate_name: '',
      previous_status_c: 'refund_request_created',
    }
  })
  return response.data
}

const setIdleTime = async payload => {
  const response = await api.post('/Api/V8/custom/customer/set-idle-time', {
    data: {
      payload
    }
  })
  return response.data
}

const setPortalTimelogs = async () => {
  const response = await api.post(
    '/Api/V8/custom/customer/set-portal-time-logs',
    {
      data: {
        payload
      }
    }
  )
  return response.data
}

const createModuleRecord = async payload => {
  const response = await api.post(
    '/Api/V8/custom/savedata/create-module-record',

    payload
  )
  return response
}

const saveFieldsVerificationData = async payload => {
  const response = await api.post(
    '/Api/V8/custom/savedata/save-fields-verification-data',
    {
      data: payload
    }
  )
  return response.data
}

const savePaymentCreditCards = async payload => {
  const response = await api.post(
    '/Api/V8/custom/savedata/save-payment-credit-cards',
    {
      data: payload
    }
  )
  return response.data
}

const saveTripData = async data => {
  const response = await api.post('/Api/V8/custom/savedata/save-trip-data', {
    data
  })
  return response.data
}

const deleteCreditCard = async CardId => {
  const response = await api.delete(
    `/Api/V8/module/gb_payment_credit_cards/${CardId}`
  )
  return response?.data
}

const deleteCreditCardDocument = async (CardId, DocId) => {
  const response = await api.delete(
    `/Api/V8/module/gb_payment_credit_cards/${CardId}/relationships/payment_credit_card_documents/${DocId}`
  )
  return response?.data
}

const resetCreditCardFields = async (CardId, documentId = null) => {
  const response = await api.post(
    '/Api/V8/custom/customer/reset-credit-card-fields',
    {
      card_id: CardId,
      ...(documentId ? { document_id: documentId } : {})
    }
  )
  return response?.data
}

const deleteArnFile = async (tripId, documentId) => {
  // Delete ARN Relation from trip
  const deleteRelationResponse = await api.delete(
    `/Api/V8/module/gb_trip/${tripId}/relationships/trip_documents/${documentId}`
  )

  // Delete ARN Document - Update document attributes
  const body = {
    data: {
      id: documentId,
      type: 'Documents',
      attributes: {
        gb_doc_type_c: ''
      }
    }
  }

  const deleteDocumentResponse = await api.patch('/Api/V8/module', body)

  return {
    deleteRelationResponse: deleteRelationResponse?.data,
    deleteDocumentResponse: deleteDocumentResponse?.data
  }
}

const deleteItinerary = async (tripId, documentId) => {
  const response = await api.delete(
    `/Api/V8/module/gb_trip/${tripId}/relationships/trip_documents/${documentId}`
  )
  return response?.data
}

const updateMiscDocument = async data => {
  const response = await api.patch(`/Api/V8/module`, {
    data: data
  })
  return response?.data
}

const savePassport = async data => {
  const response = await api.post('/Api/V8/custom/savedata/save-passport', {
    data: data
  })
  return response.data
}

const deletePassport = async (passportId, documentId) => {
  const response = await api.delete(
    `/Api/V8/module/gb_passport/${passportId}/relationships/passport_documents/${documentId}`
  )
  return response?.data
}

const deleteCruiseInfo = async (tripId, documentId) => {
  const response = await api.delete(
    `/Api/V8/module/gb_trip/${tripId}/relationships/trip_documents/${documentId}`
  )
  return response?.data
}

const deleteCruiseSchedule = async (tripId, documentId) => {
  const response = await api.delete(
    `/Api/V8/module/gb_trip/${tripId}/relationships/trip_documents/${documentId}`
  )
  return response?.data
}

const deleteBoarding = async (tripId, documentId) => {
  const response = await api.delete(
    `/Api/V8/module/gb_trip/${tripId}/relationships/trip_documents/${documentId}`
  )
  return response?.data
}

const deleteEntryStamp = async (tripId, documentId) => {
  const response = await api.delete(
    `/Api/V8/module/gb_trip/${tripId}/relationships/trip_documents/${documentId}`
  )
  return response?.data
}

const addNewPurchaseRequest = async body => {
  const response = await api.post(
    '/Api/V8/custom/savedata/create-purchase-request',
    body
  )

  return response.data
}

const savePurchaseReceiptData = async data => {
  const response = await api.post(
    '/Api/V8/custom/savedata/save-purchase-receipt-verification-data',
    {
      data
    }
  )
  return response.data
}

const savePurchaseRequestData = async data => {
  const response = await api.post(
    '/Api/V8/custom/savedata/save-purchase-request-data',
    {
      data: data
    }
  )
  return response.data
}

const deletePurchaseRequest = async id => {
  const response = await api.delete(`/Api/V8/module/gb_invoices/${id}`)
  return response.data
}

const addNewPurchasePayment = async crm_params => {
  const response = await api.post(
    '/Api/V8/custom/savedata/create-module-record',
    crm_params
  )
  return response.data
}

const deletePurchasePayment = async id => {
  const response = await api.delete(`/Api/V8/module/gb_purchase_payments/${id}`)
  return response.data
}

const deletePOSItem = async (invoiceId, id) => {
  const response = await api.delete(
    `/Api/V8/module/gb_invoices/${invoiceId}/relationships/invoice_documents/${id}`
  )
  return response.data
}

const deleteCCSlip = async (purchasePaymentId, id) => {
  const response = await api.delete(
    `/Api/V8/module/gb_purchase_payments/${purchasePaymentId}/relationships/purchase_payment_documents/${id}`
  )
  return response.data
}

const deleteFactura = async (purchaseRequestId, id) => {
  const response = await api.delete(
    `/Api/V8/module/gb_invoices/${purchaseRequestId}/relationships/invoice_documents/${id}`
  )
  return response.data
}

const deletePOSStamp = async (purchaseRequestId, id) => {
  const response = await api.delete(
    `/Api/V8/module/gb_invoices/${purchaseRequestId}/relationships/invoice_documents/${id}`
  )
  return response.data
}

const deleteOther = async (purchaseRequestId, id) => {
  const response = await api.delete(
    `/Api/V8/module/gb_invoices/${purchaseRequestId}/relationships/invoice_documents/${id}`
  )
  return response.data
}

const setStatusChange = async payload => {
  const response = await api.post(
    '/Api/V8/custom/customer/set-status-change',
    payload
  )
  return response
}

export const useCustomer = () => {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dispatch = useDispatch()
  const { logApiActivity } = useLogActivity()

  const handleCreateCustomer = async (customerData, options) => {
    setIsSubmitting(true)
    try {
      const agentId = getUserId() || ''

      const response = await logApiActivity({
        contactId: apiResponse => apiResponse?.customer?.id || null,
        agentId,
        apiEndpoint: '/Api/V8/custom/customer/create-new-customer',
        actionType: 'create_customer',
        fieldName: null,
        oldValue: null,
        newValue: null,
        requestPayload: customerData,
        apiCall: () => createCustomer(customerData)
      })

      const customerId = response?.customer?.id
      if (response?.success && customerId) {
        await createShareFileFolder(customerId)
        toast.success('Customer created successfully')
        dispatch(setCurrentTab(2))
        dispatch(setNewCustomer(true))
        navigate(`/customers/details/${customerId}`)
        options?.onSuccess?.(response)
      } else {
        toast.error('Unable to create customer')
        options?.onError?.(response)
      }
      options?.onSuccess?.(response)
      return response
    } catch (error) {
      toast.error('Unable to create customer')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteCustomer = async id => {
    setIsSubmitting(true)
    try {
      const response = await deleteCustomer(id)
      if (response?.success) {
        toast.success('Customer deleted successfully')
      } else {
        toast.error('Unable to delete customer')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete customer')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClaimCustomer = async (contactId, agentId) => {
    setIsSubmitting(true)
    try {
      const response = await claimCustomer(contactId, agentId)
      return response
    } catch (error) {
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSetIdleTime = async () => {
    setIsSubmitting(true)
    try {
      const response = await setIdleTime(payload)
      if (response?.success) {
        //toast.success('Idle time set successfully')
      } else {
        //toast.error('Unable to set idle time')
      }
      return response
    } catch (error) {
      //toast.error('Unable to set idle time')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSetPortalTimeLog = async () => {
    setIsSubmitting(true)
    try {
      const response = await setPortalTimelogs(payload)
      if (response?.success) {
        //toast.success('Portal time logs set successfully')
      } else {
        //toast.error('Unable to set portal time logs')
      }
      return response
    } catch (error) {
      //toast.error('Unable to set portal time logs')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSetCreateModuleRecord = async payload => {
    setIsSubmitting(true)
    try {
      const response = await createModuleRecord(payload)
      if (response?.data) {
        //toast.success('Card Added successfully')
      } else {
        //toast.error('Unable to Add Card')
      }
      return response
    } catch (error) {
      //toast.error('Unable to Add Card')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveFieldsVerificationData = async payload => {
    setIsSubmitting(true)
    try {
      const response = await saveFieldsVerificationData(payload)
      if (response?.success) {
        //toast.success('Fields verification data saved successfully')
      } else {
        //toast.error('Unable to save fields verification data')
      }
      return response
    } catch (error) {
      //toast.error('Unable to save fields verification data')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSavePaymentCreditCards = async payload => {
    setIsSubmitting(true)
    try {
      const response = await savePaymentCreditCards(payload)
      if (response) {
        //toast.success('Payment credit cards saved successfully')
      } else {
        //toast.error('Unable to save payment credit cards')
      }
      return response
    } catch (error) {
      //toast.error('Unable to save payment credit cards')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveTripData = async data => {
    setIsSubmitting(true)
    try {
      const response = await saveTripData(data)
      if (response?.data) {
        //toast.success('Trip card saved successfully')
      } else {
        //toast.error('Unable to Add Trip card')
      }
      return response
    } catch (error) {
      //toast.error('Unable to Add Trip card')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const DeleteCreditCard = async CardId => {
    setIsSubmitting(true)
    try {
      const response = await deleteCreditCard(CardId)
      if (response) {
        toast.success('Delete Card successfully')
      } else {
        toast.error('Unable to delete Card')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete Card')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteArnFile = async (tripId, documentId) => {
    setIsSubmitting(true)
    try {
      const response = await deleteArnFile(tripId, documentId)
      if (response) {
        toast.success('ARN file deleted successfully')
      } else {
        toast.error('Unable to delete ARN file')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete ARN file')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  // Generic trip document delete helper (used by GB Application delete flow)
  const handleDeleteTripDocument = async (tripId, documentId) => {
    setIsSubmitting(true)
    try {
      const response = await deleteArnFile(tripId, documentId)
      if (response) {
        toast.success('Document deleted successfully')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete document')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteItinerary = async (tripId, documentId) => {
    setIsSubmitting(true)
    try {
      const response = await deleteItinerary(tripId, documentId)
      if (response) {
        toast.success('Itinerary deleted successfully')
      } else {
        toast.error('Unable to delete itinerary')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete itinerary')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateMiscDocument = async data => {
    setIsSubmitting(true)
    try {
      const response = await updateMiscDocument(data)
      if (response) {
        toast.success('Misc document updated successfully')
      } else {
        toast.error('Unable to update misc document')
      }
      return response
    } catch (error) {
      toast.error('Unable to update misc document')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSavePassport = async data => {
    setIsSubmitting(true)
    try {
      const response = await savePassport(data)
      if (response) {
        //toast.success('Passport saved successfully')
      } else {
        //toast.error('Unable to save passport')
      }
      return response
    } catch (error) {
      //toast.error('Unable to save passport')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeletePassport = async (passportId, documentId) => {
    setIsSubmitting(true)
    try {
      const response = await deletePassport(passportId, documentId)
      if (response) {
        //toast.success('Passport deleted successfully')
      } else {
        //toast.error('Unable to delete passport')
      }
      return response
    } catch (error) {
      //toast.error('Unable to delete passport')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteCruiseInfo = async (tripId, documentId) => {
    setIsSubmitting(true)
    try {
      const response = await deleteCruiseInfo(tripId, documentId)
      if (response) {
        //toast.success('Cruise info deleted successfully')
      } else {
        //toast.error('Unable to delete cruise info')
      }
      return response
    } catch (error) {
      //toast.error('Unable to delete cruise info')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteCruiseSchedule = async (tripId, documentId) => {
    setIsSubmitting(true)
    try {
      const response = await deleteCruiseSchedule(tripId, documentId)
      if (response) {
        //toast.success('Cruise schedule deleted successfully')
      } else {
        //toast.error('Unable to delete cruise schedule')
      }
      return response
    } catch (error) {
      //toast.error('Unable to delete cruise schedule')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteBoarding = async (tripId, documentId) => {
    setIsSubmitting(true)
    try {
      const response = await deleteBoarding(tripId, documentId)
      if (response) {
        //toast.success('Boarding deleted successfully')
      } else {
        //toast.error('Unable to delete boarding')
      }
      return response
    } catch (error) {
      //toast.error('Unable to delete boarding')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteEntryStamp = async (tripId, documentId) => {
    setIsSubmitting(true)
    try {
      const response = await deleteEntryStamp(tripId, documentId)
      if (response) {
        //toast.success('Entry stamp deleted successfully')
      } else {
        //toast.error('Unable to delete entry stamp')
      }
      return response
    } catch (error) {
      //toast.error('Unable to delete entry stamp')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRemoveCreditCardDoc = async (CardId, DocId) => {
    setIsSubmitting(true)
    try {
      const response = await deleteCreditCardDocument(CardId, DocId)
      if (response) {
        toast.success('Credit card document deleted successfully')
      } else {
        toast.error('Unable to delete credit card document')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete credit card document')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResetCreditCardFields = async (CardId, documentId = null) => {
    if (!CardId) return null
    try {
      const response = await resetCreditCardFields(CardId, documentId)
      return response
    } catch (error) {
      console.error('Unable to reset credit card fields:', error)
      return null
    }
  }

  const handleAddNewPurchaseRequest = async payload => {
    setIsSubmitting(true)
    try {
      const response = await addNewPurchaseRequest(payload)
      if (response) {
        toast.success('Purchase request added successfully')
      } else {
        toast.error('Unable to add purchase request')
      }
      return response
    } catch (error) {
      toast.error('Unable to add purchase request')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSavePurchaseReceiptData = async data => {
    setIsSubmitting(true)
    try {
      const response = await savePurchaseReceiptData(data)
      if (response) {
        //toast.success('Purchase receipt data saved successfully')
      } else {
        //toast.error('Unable to save purchase receipt data')
      }
      return response
    } catch (error) {
      //toast.error('Unable to save purchase receipt data')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSavePurchaseRequestData = async data => {
    setIsSubmitting(true)
    try {
      const response = await savePurchaseRequestData(data)
      if (response) {
        //toast.success('Purchase request data saved successfully')
      } else {
        //toast.error('Unable to save purchase request data')
      }
      return response
    } catch (error) {
      //toast.error('Unable to save purchase request data')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeletePurchaseRequest = async id => {
    setIsSubmitting(true)
    try {
      const response = await deletePurchaseRequest(id)
      if (response) {
        toast.success('Purchase request deleted successfully')
      } else {
        toast.error('Unable to delete purchase request')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete purchase request')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddNewPurchasePayment = async crm_params => {
    setIsSubmitting(true)
    try {
      const response = await addNewPurchasePayment(crm_params)
      if (response) {
        toast.success('Purchase payment added successfully')
      } else {
        toast.error('Unable to add purchase payment')
      }
      return response
    } catch (error) {
      toast.error('Unable to add purchase payment')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeletePurchasePayment = async id => {
    setIsSubmitting(true)
    try {
      const response = await deletePurchasePayment(id)
      if (response) {
        toast.success('Purchase payment deleted successfully')
      } else {
        toast.error('Unable to delete purchase payment')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete purchase payment')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeletePOSItem = async (purchaseRequestId, id) => {
    setIsSubmitting(true)
    try {
      const response = await deletePOSItem(purchaseRequestId, id)
      if (response) {
        toast.success('POS item deleted successfully')
      } else {
        toast.error('Unable to delete POS item')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete POS item')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteCCSlip = async (purchasePaymentId, id) => {
    setIsSubmitting(true)
    try {
      const response = await deleteCCSlip(purchasePaymentId, id)
      if (response) {
        toast.success('CC slip deleted successfully')
      } else {
        toast.error('Unable to delete CC slip')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete CC slip')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteFactura = async (purchaseRequestId, id) => {
    setIsSubmitting(true)
    try {
      const response = await deleteFactura(purchaseRequestId, id)
      if (response) {
        toast.success('Factura deleted successfully')
      } else {
        toast.error('Unable to delete factura')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete factura')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteOther = async (purchaseRequestId, id) => {
    setIsSubmitting(true)
    try {
      const response = await deleteOther(purchaseRequestId, id)
      if (response) {
        toast.success('Other card deleted successfully')
      } else {
        toast.error('Unable to delete other card')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete other card')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSyncDataDocs = async contactId => {
    setIsSubmitting(true)
    try {
      const response = await handleSyncDataApi(contactId)
      if (response) {
        toast.success('Sync data successfully')
      } else {
        toast.error('Unable to sync data')
      }
      return response
    } catch (error) {
      toast.error('Unable to sync data')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSetStatusChange = async payload => {
    setIsSubmitting(true)
    try {
      const response = await setStatusChange(payload)
      if (response) {
        toast.success('Status changed successfully')
      } else {
        toast.error('Unable to change status')
      }
      return response
    } catch (error) {
      toast.error('Unable to change status')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeletePOSStamp = async (purchaseRequestId, id) => {
    setIsSubmitting(true)
    try {
      const response = await deletePOSStamp(purchaseRequestId, id)
      if (response) {
        toast.success('POS stamp deleted successfully')
      } else {
        toast.error('Unable to delete POS stamp')
      }
      return response
    } catch (error) {
      toast.error('Unable to delete POS stamp')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    handleCreateCustomer,
    handleDeleteCustomer,
    handleClaimCustomer,
    handleSetIdleTime,
    handleSetPortalTimeLog,
    handleSetCreateModuleRecord,
    handleSaveFieldsVerificationData,
    handleSavePaymentCreditCards,
    handleSaveTripData,
    DeleteCreditCard,
    handleDeleteArnFile,
    handleDeleteTripDocument,
    handleDeleteItinerary,
    handleUpdateMiscDocument,
    handleSavePassport,
    handleDeletePassport,
    handleDeleteCruiseInfo,
    handleDeleteCruiseSchedule,
    handleDeleteBoarding,
    handleDeleteEntryStamp,
    handleRemoveCreditCardDoc,
    handleResetCreditCardFields,
    handleAddNewPurchaseRequest,
    handleSavePurchaseReceiptData,
    handleSavePurchaseRequestData,
    handleDeletePurchaseRequest,
    handleAddNewPurchasePayment,
    handleDeletePurchasePayment,
    handleDeletePOSItem,
    handleDeleteCCSlip,
    handleDeleteFactura,
    handleDeleteOther,
    handleDeletePOSStamp,
    handleSyncDataDocs,
    handleSetStatusChange,
    isSubmitting
  }
}
